import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2, Users, AlertTriangle, ShoppingBag, ShoppingCart, ShieldCheck, ExternalLink, Search,
} from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 text-xs text-gray-600">
    <Icon size={14} className="text-emerald-700" />
    <span className="font-medium text-gray-900">{value ?? 0}</span>
    <span>{label}</span>
  </div>
);

export default function TenantsAdmin() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ name: "", country: "", phone: "" });
  const [saving, setSaving] = useState(false);

  const isSuper = useMemo(
    () => user?.tenant_slug === "abrah" &&
      ["managing_director", "executive_director"].includes(user?.role),
    [user]
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/tenants");
      setTenants(r.data || []);
    } catch (e) {
      if (e?.response?.status === 403)
        toast.error("Only the root tenant super-admin can manage tenants.");
      else toast.error("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isSuper) load(); else setLoading(false); }, [isSuper]);

  if (!isSuper) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center">
        <ShieldCheck size={42} className="mx-auto text-emerald-700 mb-3" />
        <h2 className="font-display text-2xl font-bold text-gray-900">Restricted Area</h2>
        <p className="text-gray-600 mt-2 text-sm">
          Tenant management is reserved for the root supermarket&apos;s executive directors.
        </p>
      </div>
    );
  }

  const toggleActive = async (t) => {
    try {
      await api.patch(`/admin/tenants/${t.slug}`, { is_active: !t.is_active });
      toast.success(`${t.name} ${!t.is_active ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const openEdit = (t) => {
    setEditTarget(t);
    setForm({ name: t.name || "", country: t.country || "", phone: t.phone || "" });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await api.patch(`/admin/tenants/${editTarget.slug}`, form);
      toast.success("Tenant updated");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const filtered = tenants.filter((t) => {
    const s = q.toLowerCase();
    return !s || t.name?.toLowerCase().includes(s) || t.slug?.toLowerCase().includes(s)
      || t.country?.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="tenants-admin-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
            Tenants
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage all supermarkets running on AEOIBCMS. {tenants.length} total · {tenants.filter(t => t.is_active !== false).length} active.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tenants…"
              className="pl-8 w-56"
              data-testid="tenants-search"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500 text-sm">No tenants match.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((t) => {
            const isActive = t.is_active !== false;
            const isRoot = t.slug === "abrah";
            return (
              <Card key={t.slug} data-testid={`tenant-card-${t.slug}`}
                className={`border ${isActive ? "border-gray-200" : "border-amber-300 bg-amber-50/40"}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-base text-gray-900">{t.name}</CardTitle>
                      <div className="text-[11px] uppercase tracking-wider text-gray-500 mt-0.5">
                        /{t.slug}{isRoot && <span className="ml-1.5 text-emerald-700">· root</span>}
                      </div>
                    </div>
                    <Badge variant={isActive ? "default" : "outline"}
                      className={isActive ? "bg-emerald-700" : "border-amber-500 text-amber-700"}>
                      {isActive ? "Active" : "Disabled"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-xs text-gray-600 space-y-1">
                    {t.country && <div>📍 {t.country}</div>}
                    {t.phone && <div>📞 {t.phone}</div>}
                    {t.created_at && <div>📅 {new Date(t.created_at).toLocaleDateString()}</div>}
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-gray-100">
                    <Stat icon={Users} label="users" value={t.stats?.users} />
                    <Stat icon={Building2} label="branches" value={t.stats?.branches} />
                    <Stat icon={AlertTriangle} label="issues" value={t.stats?.issues} />
                    <Stat icon={ShoppingBag} label="products" value={t.stats?.products} />
                    <Stat icon={ShoppingCart} label="orders" value={t.stats?.orders} />
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={isActive}
                        onCheckedChange={() => toggleActive(t)}
                        disabled={isRoot}
                        data-testid={`tenant-active-${t.slug}`}
                      />
                      <Label className="text-xs text-gray-700">
                        {isRoot ? "Root tenant (protected)" : "Login enabled"}
                      </Label>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(t)}
                        data-testid={`tenant-edit-${t.slug}`}>Edit</Button>
                      <Link to={`/site/${t.slug}`} target="_blank">
                        <Button size="sm" variant="ghost">
                          <ExternalLink size={14} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit tenant · /{editTarget?.slug}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Display name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="tenant-edit-name" />
            </div>
            <div>
              <Label className="text-xs">Country</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Contact phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="tenant-edit-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
